/**
 * Supporting document helpers for Step 5 (Add Supporting Documents).
 * Uses the same prepared-documents API as My Documents (/api/documents/*).
 */

const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
export const DOCUMENTS_BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
export const DOCUMENTS_API = `${DOCUMENTS_BACKEND_URL}/api`;

/** Step 5 attachment slot → My Documents category */
export const ATTACHMENT_ROLE_TO_DOC_CATEGORY = {
  proof_of_address: 'proof_of_address',
  government_id: 'identity_verification',
  ssn_support: 'ssn_support',
  ssn_card: 'ssn_support',
  data_breach_proof: 'other',
  identity_theft_report: 'other',
  investigation_results: 'other',
};

/** Package-level bucket when no Data Breach letter exists yet. */
export const IDENTITY_THEFT_REPORT_BUCKET_KEY = '__identity_theft_report__';

export const DATA_BREACH_LETTER_ATTACHMENT_SCOPE = {
  shared: false,
  apply_to_all_envelopes: false,
  scope: 'letter',
  data_breach_only: true,
};

export function withDataBreachLetterScope(attachment, source = 'letter_specific') {
  if (!attachment) return attachment;
  return {
    ...attachment,
    ...DATA_BREACH_LETTER_ATTACHMENT_SCOPE,
    category: 'identity_theft_report',
    source: attachment.source || source,
  };
}

export function isIdentityTheftReportAttachment(attachment) {
  return String(attachment?.category || '').toLowerCase() === 'identity_theft_report';
}

/** Upsert Identity Theft Report onto Data Breach letter buckets only (never package-wide). */
export function applyIdentityTheftReportToPerLetterMap(
  prev = {},
  dataBreachLetterIds = [],
  attachment
) {
  if (!attachment || typeof attachment !== 'object') return prev || {};
  const scoped = withDataBreachLetterScope(attachment);
  const next = { ...(prev || {}) };
  const scopedIds = new Set(attachmentIdentityIds(scoped));

  const stripMatchingIdentityTheftRows = (items = []) =>
    (Array.isArray(items) ? items : []).filter((item) => {
      if (!item || typeof item !== 'object') return false;
      if (!isIdentityTheftReportAttachment(item)) return true;
      const itemIds = attachmentIdentityIds(item);
      if (itemIds.some((id) => scopedIds.has(id))) return false;
      return !attachmentsShareIdentity(item, scoped);
    });

  if (!Array.isArray(dataBreachLetterIds) || !dataBreachLetterIds.length) {
    next[IDENTITY_THEFT_REPORT_BUCKET_KEY] = upsertPackageAttachmentList(
      stripMatchingIdentityTheftRows(next[IDENTITY_THEFT_REPORT_BUCKET_KEY]),
      scoped
    );
    return next;
  }

  delete next[IDENTITY_THEFT_REPORT_BUCKET_KEY];
  dataBreachLetterIds.forEach((letterId) => {
    if (typeof letterId !== 'string' || !letterId.length) return;
    next[letterId] = upsertPackageAttachmentList(
      stripMatchingIdentityTheftRows(next[letterId]),
      scoped
    );
  });
  return next;
}

export function removeIdentityTheftReportFromPerLetterMap(prev = {}) {
  const next = { ...prev };
  Object.keys(next).forEach((letterId) => {
    next[letterId] = (next[letterId] || []).filter(
      (item) => !isIdentityTheftReportAttachment(item)
    );
  });
  delete next[IDENTITY_THEFT_REPORT_BUCKET_KEY];
  return next;
}

/** Move bucket-stored Identity Theft Report onto Data Breach letter ids after restore. */
export function migrateIdentityTheftReportBucket(perLetterAttachments = {}, dataBreachLetterIds = []) {
  const bucketItems = perLetterAttachments[IDENTITY_THEFT_REPORT_BUCKET_KEY];
  if (!Array.isArray(bucketItems) || !bucketItems.length || !dataBreachLetterIds.length) {
    return perLetterAttachments;
  }
  const attachment = bucketItems.find(isIdentityTheftReportAttachment) || bucketItems[0];
  const withoutBucket = { ...perLetterAttachments };
  delete withoutBucket[IDENTITY_THEFT_REPORT_BUCKET_KEY];
  return applyIdentityTheftReportToPerLetterMap(withoutBucket, dataBreachLetterIds, attachment);
}

export function attachmentRoleToDocumentCategory(role) {
  return ATTACHMENT_ROLE_TO_DOC_CATEGORY[role] || role;
}

/** Resolve server file URL from /documents/upload response (incl. HEIC conversion fields). */
export function serverUrlFromUploadResponse(uploadData) {
  if (!uploadData) return null;
  const direct = uploadData.file_url || uploadData.cropped_url;
  if (direct && typeof direct === 'string' && !direct.startsWith('blob:')) {
    return direct.startsWith('/api') ? `${DOCUMENTS_BACKEND_URL}${direct}` : direct;
  }
  return resolvePreparedDocumentUrl(uploadData.document);
}

/** Build attachment readiness probe from backend HEIC conversion upload response. */
export function attachmentFromHeicUploadResponse(uploadData) {
  const url = serverUrlFromUploadResponse(uploadData);
  if (!url || !uploadData?.document_id) return null;
  return {
    document_id: uploadData.document_id,
    file_url: url,
    cropped_url: url,
    preview_url: url,
    server_url: url,
    source: 'backend_storage',
    upload_status: 'ready',
    is_server_saved: true,
    is_attachment_ready: true,
    converted_from_heic: Boolean(uploadData.converted_from_heic),
    mime_type: uploadData.content_type || 'image/jpeg',
  };
}

export function resolvePreparedDocumentUrl(doc) {
  if (!doc) return null;

  const versionKeys = ['approved_file', 'manual_adjusted_file', 'ai_cropped_file', 'original_file'];
  for (const key of versionKeys) {
    const version = doc[key];
    const fileUrl = version?.file_url;
    if (fileUrl) {
      return fileUrl.startsWith('/api') ? `${DOCUMENTS_BACKEND_URL}${fileUrl}` : fileUrl;
    }
  }

  const fallback = doc.cropped_url || doc.file_url || doc.preview_url || doc.thumbnail_url;
  if (!fallback) return null;
  if (fallback.startsWith('/api')) return `${DOCUMENTS_BACKEND_URL}${fallback}`;
  return fallback;
}

/** URL for the untouched original upload — used when re-opening the crop editor. */
export function resolveOriginalDocumentUrl(doc) {
  if (!doc) return null;
  const fileUrl = doc.original_file?.file_url;
  if (!fileUrl) return null;
  return fileUrl.startsWith('/api') ? `${DOCUMENTS_BACKEND_URL}${fileUrl}` : fileUrl;
}

/** Resolve original image URL from a Step 5 attachment object. */
export function resolveAttachmentOriginalUrl(attachment) {
  if (!attachment) return null;
  if (typeof attachment.original_file === 'string' && attachment.original_file) {
    return attachment.original_file;
  }
  if (attachment.original_file_url) {
    return attachment.original_file_url;
  }
  if (attachment.crop_metadata?.original_file_url) {
    return attachment.crop_metadata.original_file_url;
  }
  return null;
}

/** Build crop editor metadata from a prepared document record. */
export function cropMetadataFromPreparedDocument(doc) {
  if (!doc) return null;
  const settings = doc.current_crop_settings;
  if (!settings || typeof settings !== 'object') {
    return doc.crop_metadata || null;
  }
  return {
    ...settings,
    crop_box_percent: {
      x: (settings.x ?? 0) / 100,
      y: (settings.y ?? 0) / 100,
      width: (settings.width ?? 100) / 100,
      height: (settings.height ?? 100) / 100,
    },
    rotation_degrees: settings.rotation ?? 0,
    original_file_url: resolveOriginalDocumentUrl(doc),
  };
}

/** Local Step 5 placeholder id before the server assigns a real document_id. */
export function isLocalPendingDocumentId(docId) {
  return typeof docId === 'string' && /^doc_\d+_/.test(docId);
}

export function isBlobUrl(url) {
  return typeof url === 'string' && url.startsWith('blob:');
}

/** True when attachment has a non-blob server URL or a persisted server document id. */
export function hasServerBackedFileReference(attachment) {
  if (!attachment || typeof attachment !== 'object' || Array.isArray(attachment)) return false;

  const urlCandidates = [
    attachment.server_url,
    attachment.cropped_url,
    attachment.file_url,
    attachment.document_url,
    attachment.storage_url,
    attachment.preview_url,
  ].filter((url) => typeof url === 'string' && url.length > 0);

  for (const url of urlCandidates) {
    if (isBlobUrl(url)) continue;
    if (url.startsWith('/api/documents/files/') || url.includes('/documents/files/')) return true;
    if (url.startsWith('http://') || url.startsWith('https://')) return true;
    if (url.startsWith('/api/')) return true;
  }

  const preparedUrl = resolvePreparedDocumentUrl(attachment);
  if (typeof preparedUrl === 'string' && preparedUrl.length > 0 && !isBlobUrl(preparedUrl)) {
    return true;
  }

  const docId = attachment.server_document_id || attachment.document_id || attachment.id;
  if (docId && typeof docId === 'string' && !isLocalPendingDocumentId(docId)) {
    return true;
  }

  return false;
}

/** Resolve SSN Card from package identity support (handles ssn_card vs ssn_support keys). */
export function getPackageSsnCardAttachment(packageIdentitySupport) {
  if (!packageIdentitySupport) return null;
  return packageIdentitySupport.ssn_card || packageIdentitySupport.ssn_support || null;
}

export const ATTACHMENT_UPLOAD_LINK_TIMEOUT_MS = 120000;
export const ATTACHMENT_PREVIEW_TIMEOUT_MS = 30000;

/** Step 5 primary button — only in-flight upload/link operations block Continue. */
export function isCriticalUploadInProgress(attachmentUploadsInFlight) {
  return Number(attachmentUploadsInFlight) > 0;
}

export function isAttachmentUploadInProgress(attachment) {
  if (!attachment || typeof attachment !== 'object' || Array.isArray(attachment)) return false;
  if (attachmentHasPersistedServerSave(attachment)) return false;
  return attachmentHasUploadingStatus(attachment);
}

/** Primitive id keys only — never call upsert/dedupe/share helpers. */
export function attachmentIdentityIds(attachment) {
  if (!attachment || typeof attachment !== 'object' || Array.isArray(attachment)) return [];

  const cropMeta = attachment.crop_metadata;
  const sourceDocId =
    cropMeta && typeof cropMeta === 'object' && !Array.isArray(cropMeta)
      ? cropMeta.source_document_id
      : null;

  const ids = [
    attachment.document_id,
    attachment.client_document_id,
    attachment.server_document_id,
    attachment.source_document_id,
    sourceDocId,
  ].filter((id) => typeof id === 'string' && id.length > 0);

  return [...new Set(ids)];
}

function normalizeAttachmentStem(value) {
  if (!value) return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/_cropped\.(jpg|jpeg|png|webp)$/i, '')
    .replace(/\.(jpg|jpeg|png|webp|pdf|heic)$/i, '');
}

function attachmentHasUploadingStatus(attachment) {
  if (!attachment || typeof attachment !== 'object') return false;
  return attachment.upload_status === 'uploading' || attachment.upload_status === 'pending';
}

/** Shared primitive checks for persisted server save — no upload-progress helpers. */
function attachmentHasPersistedServerSave(attachment) {
  if (!attachment || typeof attachment !== 'object' || Array.isArray(attachment)) return false;
  if (attachment.upload_status === 'failed') return false;

  const hasServerRef = hasServerBackedFileReference(attachment);
  const persistedSource =
    attachment.is_server_saved === true ||
    attachment.upload_status === 'ready' ||
    attachment.source === 'backend_storage' ||
    attachment.source === 'existing_document' ||
    attachment.source === 'package_level' ||
    attachment.source === 'system_default' ||
    attachment.is_existing_document_reference;

  return Boolean(hasServerRef && persistedSource);
}

function attachmentIsPackageWideScope(attachment) {
  if (!attachment || typeof attachment !== 'object') return false;
  if (attachment.scope === 'package') return true;
  if (attachment.apply_to_all_envelopes === true || attachment.shared === true) return true;
  const src = String(attachment.source || '');
  return src === 'package_level' || src === 'package_shared';
}

/** True when two attachments represent the same underlying document. */
export function attachmentsShareIdentity(a, b) {
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (a === b) return true;

  const idsA = new Set(attachmentIdentityIds(a));
  const idsB = attachmentIdentityIds(b);
  if (idsB.some((id) => idsA.has(id))) return true;

  const catA = String(a.category || 'other').toLowerCase();
  const catB = String(b.category || 'other').toLowerCase();
  if (catA !== catB) return false;

  const stemA = normalizeAttachmentStem(a.title || a.filename);
  const stemB = normalizeAttachmentStem(b.title || b.filename);
  if (!stemA || !stemB || stemA !== stemB) return false;

  return attachmentIsPackageWideScope(a) && attachmentIsPackageWideScope(b);
}

function preferAttachmentRow(existing, incoming) {
  if (isServerSavedDocument(incoming) && !isServerSavedDocument(existing)) return incoming;
  if (isServerSavedDocument(existing) && !isServerSavedDocument(incoming)) return existing;
  if (existing.upload_status === 'uploading' && incoming.upload_status === 'ready') return incoming;
  if (incoming.upload_status === 'uploading' && existing.upload_status === 'ready') return existing;
  return incoming;
}

/** Replace or append by document identity (handles local id -> server id transitions). */
export function upsertPackageAttachmentList(list, attachment, { replacingDocId = null } = {}) {
  if (!attachment || typeof attachment !== 'object') return Array.isArray(list) ? list : [];
  const safeList = Array.isArray(list) ? list : [];

  const replaceIds = new Set(attachmentIdentityIds(attachment));
  if (typeof replacingDocId === 'string' && replacingDocId.length > 0) {
    replaceIds.add(replacingDocId);
  }

  const withoutMatches = safeList.filter((item) => {
    if (!item || typeof item !== 'object') return true;
    const itemIds = attachmentIdentityIds(item);
    if (itemIds.some((id) => replaceIds.has(id))) return false;
    return !attachmentsShareIdentity(item, attachment);
  });

  return [...withoutMatches, attachment];
}

/** Dedupe a merged attachment list for display (shared + per-letter). */
export function dedupePackageAttachmentList(list) {
  const safeList = Array.isArray(list) ? list : [];
  const result = [];

  for (const attachment of safeList) {
    if (!attachment || typeof attachment !== 'object') continue;

    const dupIndex = result.findIndex((existing) => attachmentsShareIdentity(existing, attachment));
    if (dupIndex >= 0) {
      result[dupIndex] = preferAttachmentRow(result[dupIndex], attachment);
    } else {
      result.push(attachment);
    }
  }

  return result;
}

/** Prefer persisted server URLs; blob URLs are optional preview-only. */
export function resolveStep5AttachmentPreviewUrl(attachment) {
  if (!attachment) return null;

  const serverCandidates = [
    attachment.server_url,
    attachment.preview_url,
    attachment.cropped_url,
    attachment.file_url,
    resolvePreparedDocumentUrl(attachment),
  ];

  for (const url of serverCandidates) {
    if (typeof url === 'string' && url && !isBlobUrl(url)) {
      return url.startsWith('/api') ? `${DOCUMENTS_BACKEND_URL}${url}` : url;
    }
  }

  const blobCandidates = [attachment.preview_url, attachment.cropped_url, attachment.file_url];
  return blobCandidates.find(isBlobUrl) || null;
}

/** Apply terminal upload/link fields — never leave upload_status stuck on uploading. */
export function finalizeAttachmentUploadFields(
  attachment,
  { serverResult = null, failureMessage = null } = {}
) {
  if (!attachment) return attachment;

  if (serverResult) {
    return {
      ...attachment,
      ...serverResult,
      client_document_id: attachment.client_document_id || attachment.document_id,
      upload_status: 'ready',
      preview_status: attachment.preview_status === 'error' ? 'error' : 'loading',
      upload_error: null,
    };
  }

  return {
    ...attachment,
    source: 'blob_url_local_only',
    upload_status: 'failed',
    preview_status: 'error',
    is_server_saved: false,
    upload_error: failureMessage || attachment.upload_error || null,
  };
}

/** Human-readable reason when an attachment is not package-ready (for validation/debug). */
export function getAttachmentReadinessReason(attachment) {
  if (!attachment) return 'missing attachment';
  if (isServerSavedDocument(attachment)) return null;
  if (isAttachmentUploadInProgress(attachment)) return 'upload in progress';
  if (attachment.upload_status === 'failed') return 'upload failed';
  if (attachment.source === 'blob_url_local_only' || attachment.source === 'pending_upload') {
    return 'local preview only — not saved to server';
  }
  if (
    (isBlobUrl(attachment.file_url) || isBlobUrl(attachment.cropped_url)) &&
    !hasServerBackedFileReference(attachment)
  ) {
    return 'blob preview URL only';
  }
  if (isLocalPendingDocumentId(attachment.document_id) && !hasServerBackedFileReference(attachment)) {
    return 'pending local document id without server reference';
  }
  if (!hasServerBackedFileReference(attachment)) return 'no server file reference';
  return null;
}

/** True when the attachment references a persisted prepared document (not a browser blob). */
export function isServerSavedDocument(attachment) {
  if (!attachment || typeof attachment !== 'object' || Array.isArray(attachment)) return false;
  if (attachment.upload_status === 'failed') return false;

  if (attachmentHasPersistedServerSave(attachment)) {
    return true;
  }

  if (attachmentHasUploadingStatus(attachment)) {
    return false;
  }

  if (attachment.source === 'blob_url_local_only' || attachment.source === 'pending_upload') {
    return false;
  }

  return hasServerBackedFileReference(attachment);
}

export function preparedDocumentToPackageAttachment(doc, attachmentRole) {
  const url = resolvePreparedDocumentUrl(doc);
  const originalUrl = resolveOriginalDocumentUrl(doc);
  const cropMetadata = cropMetadataFromPreparedDocument(doc);
  return {
    document_id: doc.document_id,
    client_document_id: doc.document_id,
    title: doc.title || attachmentRole.replace(/_/g, ' '),
    category: attachmentRole,
    file_url: url,
    cropped_url: url,
    preview_url: url,
    thumbnail_url: url,
    original_file_url: originalUrl,
    original_file: originalUrl,
    crop_metadata: cropMetadata || undefined,
    cropped: true,
    included: true,
    source: 'existing_document',
    is_existing_document_reference: true,
    is_server_saved: true,
    upload_status: 'ready',
    status: doc.status,
    is_attachment_ready: doc.is_attachment_ready,
  };
}

export function pickBestPreparedDocument(items, documentCategory) {
  if (!Array.isArray(items) || !items.length) return null;

  const score = (doc) => {
    if (doc.status === 'approved' || doc.status === 'verified') return 4;
    if (doc.is_attachment_ready) return 3;
    if (doc.status === 'cropped_draft') return 2;
    if (doc.status === 'uploaded' || doc.status === 'pending_review') return 1;
    return 0;
  };

  const matches = items.filter((d) => d.category === documentCategory);
  if (!matches.length) return null;

  return [...matches].sort((a, b) => {
    const diff = score(b) - score(a);
    if (diff !== 0) return diff;
    const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
    const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
    return bTime - aTime;
  })[0];
}

/** Load shared identity docs from My Documents for Step 5 hydration. */
export async function loadSharedIdentityDocumentsFromLibrary(axios, token, { limit = 50 } = {}) {
  const response = await axios.get(`${DOCUMENTS_API}/documents/list`, {
    params: { limit },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data?.items || [];
}

export function hydratePackageIdentitySupportFromLibrary(items) {
  const support = {};

  const poa = pickBestPreparedDocument(items, 'proof_of_address');
  if (poa) {
    support.proof_of_address = preparedDocumentToPackageAttachment(poa, 'proof_of_address');
  }

  const gov = pickBestPreparedDocument(items, 'identity_verification');
  if (gov) {
    support.government_id = preparedDocumentToPackageAttachment(gov, 'government_id');
  }

  const ssn = pickBestPreparedDocument(items, 'ssn_support');
  if (ssn) {
    support.ssn_card = preparedDocumentToPackageAttachment(ssn, 'ssn_support');
  }

  return support;
}
